import { apiWorkspaceRepository } from './apiRepository'
import type { WorkspaceRepository } from './repository'
import type {
  WorkspaceClipboard,
  WorkspaceNode,
  WorkspaceQuota,
  WorkspaceUploadInput,
} from './types'

const repository: WorkspaceRepository = apiWorkspaceRepository

let clipboard: WorkspaceClipboard | null = null

export const workspaceService = {
  list(
    productCode: string,
    folderSlug: string,
    parentId: string | null,
  ): Promise<WorkspaceNode[]> {
    return repository.list(productCode, folderSlug, parentId)
  },
  getQuota(): Promise<WorkspaceQuota> {
    return repository.getQuota()
  },
  getPath(
    productCode: string,
    folderSlug: string,
    nodeId: string | null,
  ): Promise<WorkspaceNode[]> {
    return repository.getPath(productCode, folderSlug, nodeId)
  },
  createFolder(
    productCode: string,
    folderSlug: string,
    parentId: string | null,
    name?: string,
  ): Promise<WorkspaceNode> {
    return repository.createFolder(productCode, folderSlug, parentId, name)
  },
  uploadFiles(
    productCode: string,
    folderSlug: string,
    parentId: string | null,
    files: WorkspaceUploadInput[],
  ): Promise<WorkspaceNode[]> {
    return repository.uploadFiles(productCode, folderSlug, parentId, files)
  },
  rename(
    productCode: string,
    folderSlug: string,
    id: string,
    name: string,
  ): Promise<WorkspaceNode> {
    return repository.rename(productCode, folderSlug, id, name)
  },
  remove(productCode: string, folderSlug: string, ids: string[]): Promise<void> {
    return repository.remove(productCode, folderSlug, ids)
  },
  move(
    sourceCode: string,
    sourceSlug: string,
    ids: string[],
    targetCode: string,
    targetSlug: string,
    targetParentId: string | null,
  ): Promise<void> {
    return repository.move(
      sourceCode,
      sourceSlug,
      ids,
      targetCode,
      targetSlug,
      targetParentId,
    )
  },
  copy(
    sourceCode: string,
    sourceSlug: string,
    ids: string[],
    targetCode: string,
    targetSlug: string,
    targetParentId: string | null,
  ): Promise<WorkspaceNode[]> {
    return repository.copy(
      sourceCode,
      sourceSlug,
      ids,
      targetCode,
      targetSlug,
      targetParentId,
    )
  },
  cutToClipboard(productCode: string, folderSlug: string, ids: string[]): void {
    clipboard = { operation: 'cut', productCode, folderSlug, ids: [...ids] }
  },
  copyToClipboard(productCode: string, folderSlug: string, ids: string[]): void {
    clipboard = { operation: 'copy', productCode, folderSlug, ids: [...ids] }
  },
  getClipboard(): WorkspaceClipboard | null {
    return clipboard
  },
  async paste(
    productCode: string,
    folderSlug: string,
    parentId: string | null,
  ): Promise<void> {
    if (!clipboard || clipboard.ids.length === 0) return
    if (clipboard.operation === 'copy') {
      await repository.copy(
        clipboard.productCode,
        clipboard.folderSlug,
        clipboard.ids,
        productCode,
        folderSlug,
        parentId,
      )
      return
    }
    await repository.move(
      clipboard.productCode,
      clipboard.folderSlug,
      clipboard.ids,
      productCode,
      folderSlug,
      parentId,
    )
    clipboard = null
  },
}

export type {
  WorkspaceClipboard,
  WorkspaceNode,
  WorkspaceNodeKind,
  WorkspaceQuota,
  WorkspaceUploadInput,
} from './types'
export { WORKSPACE_LOCAL_STORAGE_KEY } from './storageKey'
